import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { Dropdown } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Notification, NotificationStatusId, NotificationTypeId } from '@packages/models/api';
import { getLogger } from '@packages/core/logging';
import { notificationsService } from '@web/services/singletons';
import { useContentOverlayState } from '@web/core/hooks';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { useAuthState } from '@packages/contexts/auth';
import { hasStisThirdPartyRole } from '@web/utils/can-view-stis';
import {
    useNotificationsState,
    useNotificationsDispatch,
    notificationsActions,
} from '@packages/contexts/notifications';
import {
    Typography,
    LoadingCircle,
    Icon,
    Button,
    ListItemNotification,
    NotificationListItem,
} from '@packages/ui/shared';
import ActionDropdown from './action-dropdown';
import colors from '@packages/core/styles/colors';
import ReactDOM from 'react-dom';

const logger = getLogger('web');

const useStyles = createUseStyles({
    container: {
        padding: '24px',
    },
    notificationsPanel: {
        backgroundColor: colors.white,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 400,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
        zIndex: 5,
    },
    notificationsPanelScrollContainer: {
        flexGrow: 1,
        overflowY: 'auto',
    },

    notificationsPanelHeader: {
        alignItems: 'center',
        backgroundColor: colors.grayOne,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        justifyContent: 'space-between',
        padding: '14px 24px',
    },
    notificationsPanelCenterContainer: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
    },
    notificationGroupHeader: {
        backgroundColor: colors.white,
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    closeButton: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    footer: {
        alignItems: 'center',
        backgroundColor: colors.white,
        borderTop: `1px solid ${colors.grayThree}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: '89px',
        justifyContent: 'center',
        paddingTop: '17px',
        textAlign: 'center',
    },
});

type Props = {
    /**
     * @desc Note: This controls close panel functionality
     */
    handleNotificationsPress: () => void;

    refetchNotifications: () => void;

    // Handles onClick onto notification settings tab
    openNotificationsSettingsModal: () => void;
};

const NotificationsPanel = (props: Props) => {
    const { refetchNotifications } = props;

    const classes = useStyles();
    const { toggleOverlay } = useContentOverlayState();
    const navigate = useNavigate();
    const location = useLocation();
    const vinSessionState = useVinSessionState();
    const { notifications: contextNotifications } = useNotificationsState();
    const notificationsDispatch = useNotificationsDispatch();
    const { account } = useAuthState();
    const isThirdPartyUser = hasStisThirdPartyRole(account);

    const [isLoading, setIsLoading] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const notificationGroups = useMemo(() => {
        return notifications.reduce((groupsArray: string[], notification) => {
            if (!groupsArray.includes(notification.notificationGroupingIdDescription)) {
                groupsArray.push(notification.notificationGroupingIdDescription);
            }

            return groupsArray;
        }, []);
    }, [notifications]);

    const getNotifications = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await notificationsService.fetchNotifications(undefined, true);

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }

            setNotifications(response.data.notifications);
            notificationsDispatch(
                notificationsActions.updateNotifications({
                    notifications: response.data.notifications,
                })
            );
            refetchNotifications();
        } catch (error) {
            window.alert(error.message);
        }

        setIsLoading(false);
    }, [notificationsDispatch]);

    useEffect(() => {
        getNotifications();
    }, [getNotifications]);

    const markNotificationsAsRead = useCallback(
        async (notificationIds: string[]) => {
            await notificationsService
                .updateNotifications({
                    notifications: notificationIds.map((notificationId) => ({
                        notificationId: notificationId,
                        notificationStatusId: NotificationStatusId.Read,
                    })),
                })
                .then((response) => {
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }

                    setNotifications(
                        notifications.map((n) => {
                            const updated = response.data.notifications.find(
                                (x) => x.notificationId === n.notificationId
                            );

                            if (updated) {
                                return updated;
                            }

                            return n;
                        })
                    );

                    refetchNotifications();
                });
        },
        [notifications, refetchNotifications]
    );

    const handleMarkAllAsReadButtonPress = useCallback(async () => {
        try {
            await markNotificationsAsRead(
                notifications
                    .filter(
                        (notification) =>
                            notification.notificationId &&
                            notification.notificationStatusId !== NotificationStatusId.Read
                    )
                    .map((notification) => notification.notificationId)
            );
            await getNotifications();
        } catch (error) {
            window.alert(error.message);
        }
    }, [getNotifications, markNotificationsAsRead, notifications]);

    const handleClearAllButtonPress = useCallback(async () => {
        try {
            await notificationsService.deleteNotifications(
                notifications.map((notification) => notification.notificationId)
            );

            await getNotifications();
        } catch (error) {
            window.alert(error.message);
        }
    }, [getNotifications, notifications]);

    const handleNotificationPress = useCallback(
        async (notification: Notification) => {
            if (!notification) return;

            try {
                const { notificationId, link, notificationTypeId, data } = notification;

                if (notificationTypeId === NotificationTypeId.OtpwAssignment) {
                    await markNotificationsAsRead([notificationId]);
                    refetchNotifications();
                    toggleOverlay('notifications');

                    if (vinSessionState.selectedVin) {
                        const vinDashboardPath = `/vin/${vinSessionState.selectedVin.vinRecord.vin}?highlightBanner=true`;
                        navigate(vinDashboardPath);
                    } else if (location.pathname === '/') {
                        window.dispatchEvent(new CustomEvent('highlightSecurityBanner'));
                        window.dispatchEvent(new CustomEvent('openAuthorizationCodeModal'));
                    } else {
                        navigate('/?highlightBanner=true');
                    }
                    return;
                }

                if (!link) {
                    window.alert('Notification has no link');
                    return;
                }

                await markNotificationsAsRead([notificationId]);
                refetchNotifications();
                const destination = link.replace(window.location.origin, '');
                toggleOverlay('notifications');

                const newTab = window.open(destination, '_blank');
                if (newTab) {
                    newTab.focus();
                } else {
                    window.alert('Popup blocked. Please allow popups for this site.');
                }
            } catch (error) {
                logger.error(`Failed to handle the notification [${notification.notificationId}]`, {
                    error,
                    notificationId: notification.notificationId,
                });
                window.alert(error instanceof Error ? error.message : 'An unexpected error occurred');
            }
        },
        [
            markNotificationsAsRead,
            refetchNotifications,
            toggleOverlay,
            navigate,
            location.pathname,
            vinSessionState.selectedVin,
        ]
    );

    const rightPanelDiv = document.getElementById('right-side-panel');

    if (!rightPanelDiv) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className={classes.notificationsPanel}>
            <div className={classes.notificationsPanelHeader}>
                <Typography>Notifications</Typography>
                <ActionDropdown drop="down" alignRight>
                    {!isThirdPartyUser && (
                        <Dropdown.Item onClick={props.openNotificationsSettingsModal}>
                            <Typography variant="default">View Settings</Typography>
                        </Dropdown.Item>
                    )}
                    <Dropdown.Item onClick={handleMarkAllAsReadButtonPress}>
                        <Typography variant="default">Mark all as read</Typography>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleClearAllButtonPress}>
                        <Typography variant="default">Clear all</Typography>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={refetchNotifications}>
                        <Typography variant="default">Refetch</Typography>
                    </Dropdown.Item>
                </ActionDropdown>
                <div className={classes.closeButton} onClick={props.handleNotificationsPress}>
                    <Icon name="x-close" />
                </div>
            </div>
            <div className={classes.notificationsPanelScrollContainer}>
                {isLoading && (
                    <div className={classes.notificationsPanelCenterContainer}>
                        <LoadingCircle size={64} borderWidth={8} borderColor={colors.grayOne} />
                    </div>
                )}

                {!isLoading &&
                    notificationGroups.length > 0 &&
                    notificationGroups.map((group, index) => (
                        <div key={`${group}-${index}`}>
                            <div className={classes.notificationGroupHeader}>
                                <Typography variant="h6">{group}</Typography>
                            </div>
                            {notifications
                                .filter((notification) => notification.notificationGroupingIdDescription === group)
                                .map((notification) => {
                                    return notification.notificationTypeId === NotificationTypeId.CvtDiagnostics ? (
                                        <ListItemNotification
                                            key={notification.notificationId}
                                            item={notification}
                                            onPress={handleNotificationPress}
                                        />
                                    ) : (
                                        <NotificationListItem
                                            key={notification.notificationId}
                                            item={notification}
                                            onPress={handleNotificationPress}
                                        />
                                    );
                                })}
                        </div>
                    ))}

                {!isLoading && notificationGroups.length <= 0 && (
                    <div className={classes.notificationsPanelCenterContainer}>
                        <div style={{ marginBottom: 32 }}>
                            <Icon hero name="bell" />
                        </div>
                        <Typography variant="h3" color="textDarkTertiary">
                            No Notifications
                        </Typography>
                    </div>
                )}
            </div>
            {!isThirdPartyUser && (
                <div className={classes.container}>
                    <div className={classes.footer}>
                        <Typography variant="default" color="blueOne">
                            Not seeing notifications you're expecting?
                        </Typography>
                        <Button variant="outlinePrimary" onPress={props.openNotificationsSettingsModal}>
                            Check Notification Settings
                        </Button>
                    </div>
                </div>
            )}
        </div>,
        rightPanelDiv
    );
};

export default NotificationsPanel;
